import React, { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';

// ─── Animation Variants ───────────────────────────────────────────────────────
const fadeInUp = {
	hidden: { opacity: 0, y: 32 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const staggerContainer = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};
const scaleIn = {
	hidden: { opacity: 0, scale: 0.94 },
	visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: 'easeOut' } },
};

// ─── Brand Tokens ─────────────────────────────────────────────────────────────
const C = {
	bg:       '#ffffff',
	surface:  '#f4f5f9',
	border:   'rgba(0,0,0,0.08)',
	primary:  '#7c6deb',
	teal:     '#46bcaa',
	textPri:  '#0f0f1a',
	textSec:  'rgba(15,15,26,0.55)',
	textMeta: 'rgba(15,15,26,0.35)',
} as const;

// ─── Download Badges ──────────────────────────────────────────────────────────
const DownloadBadges: React.FC<{ size?: 'sm' | 'md' }> = ({ size = 'md' }) => {
	const h = size === 'sm' ? '40px' : '48px';
	const base: React.CSSProperties = {
		display: 'inline-flex', alignItems: 'center', gap: '10px',
		background: '#000', border: `1.5px solid rgba(255,255,255,0.2)`,
		borderRadius: '10px', padding: '8px 16px', cursor: 'pointer',
		transition: 'all 0.22s', textDecoration: 'none', height: h, boxSizing: 'border-box',
	};
	const hover = (e: React.MouseEvent, on: boolean) => {
		const el = e.currentTarget as HTMLElement;
		el.style.borderColor = on ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)';
		el.style.transform   = on ? 'translateY(-2px)' : 'none';
	};
	return (
		<div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
			<a href="#" style={base} onMouseEnter={e => hover(e, true)} onMouseLeave={e => hover(e, false)}>
				<svg width="22" height="24" viewBox="0 0 22 24" fill="none">
					<path d="M0.432 0.358C0.161 0.644 0 1.085 0 1.658V22.342C0 22.915 0.161 23.356 0.432 23.642L0.502 23.71L12.124 12.088V11.912L0.502 0.29L0.432 0.358Z" fill="url(#gp1)"/>
					<path d="M16.057 15.924L12.124 11.988V11.812L16.057 7.876L16.145 7.927L20.836 10.583C22.155 11.33 22.155 12.55 20.836 13.297L16.145 15.873L16.057 15.924Z" fill="url(#gp2)"/>
					<path d="M16.145 15.873L12.124 11.9L0.432 23.642C0.873 24.109 1.598 24.168 2.412 23.71L16.145 15.873Z" fill="url(#gp3)"/>
					<path d="M16.145 7.927L2.412 0.09C1.598-0.368 0.873-0.309 0.432 0.158L12.124 11.9L16.145 7.927Z" fill="url(#gp4)"/>
					<defs>
						<linearGradient id="gp1" x1="11.346" y1="1.308" x2="-4.507" y2="17.161" gradientUnits="userSpaceOnUse"><stop stopColor="#00A0FF"/><stop offset="1" stopColor="#00E3FF"/></linearGradient>
						<linearGradient id="gp2" x1="22.826" y1="11.9" x2="-0.176" y2="11.9" gradientUnits="userSpaceOnUse"><stop stopColor="#FFE000"/><stop offset="1" stopColor="#FF9C00"/></linearGradient>
						<linearGradient id="gp3" x1="14.167" y1="13.742" x2="-7.913" y2="35.822" gradientUnits="userSpaceOnUse"><stop stopColor="#FF3A44"/><stop offset="1" stopColor="#C31162"/></linearGradient>
						<linearGradient id="gp4" x1="-1.959" y1="-8.671" x2="8.765" y2="2.053" gradientUnits="userSpaceOnUse"><stop stopColor="#32A071"/><stop offset="1" stopColor="#00F076"/></linearGradient>
					</defs>
				</svg>
				<div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
					<span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '9px', letterSpacing: '0.3px' }}>GET IT ON</span>
					<span style={{ color: '#fff', fontSize: size === 'sm' ? '13px' : '15px', fontWeight: '700' }}>Google Play</span>
				</div>
			</a>
			<a href="#" style={base} onMouseEnter={e => hover(e, true)} onMouseLeave={e => hover(e, false)}>
				<svg width="18" height="22" viewBox="0 0 814 1000" fill="white">
					<path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-38.8-155.5-127.4C46 790.5 0 665.3 0 541.9 0 324.7 133.4 198.3 264.4 198.3c70.1 0 127.9 46.7 171.7 46.7 42.4 0 108.5-49.4 188.3-49.4 30.2 0 108.2 2.6 168.6 81zM554.1 158.5c-23.6-28.4-40.8-68-40.8-107.4 0-5.8.6-11.6 1.3-17.4 38.8 1.3 86.1 26 114.4 55.7 23.6 25.4 43.4 64.7 43.4 104.1 0 6.4-.6 13-1.3 19.4-2.9.7-7.3 1.3-11.6 1.3-35.5 0-81.5-23.5-105.4-55.7z"/>
				</svg>
				<div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
					<span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '9px', letterSpacing: '0.3px' }}>DOWNLOAD ON THE</span>
					<span style={{ color: '#fff', fontSize: size === 'sm' ? '13px' : '15px', fontWeight: '700' }}>App Store</span>
				</div>
			</a>
		</div>
	);
};

// ─── App Preview Frame ────────────────────────────────────────────────────────
const AppPreviewFrame: React.FC = () => (
	<motion.div
		initial={{ opacity: 0, y: 40 }}
		animate={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.9, delay: 0.3, ease: 'easeOut' }}
		style={{ position: 'relative', width: '258px', flexShrink: 0 }}>
		{/* Phone shell */}
		<div style={{
			width: '258px', background: C.bg,
			borderRadius: '44px', border: `8px solid #13132a`,
			boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 48px 96px rgba(0,0,0,0.75)`,
			overflow: 'hidden',
		}}>
			{/* Status bar */}
			<div style={{ background: '#060610', padding: '14px 20px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<span style={{ color: '#fff', fontSize: '11px', fontWeight: '600' }}>9:41</span>
				<div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
					{[3,5,7,9].map(h => <div key={h} style={{ width: '2.5px', height: `${h}px`, background: 'rgba(255,255,255,0.7)', borderRadius: '1px' }} />)}
					<div style={{ width: '18px', height: '9px', border: '1px solid rgba(255,255,255,0.45)', borderRadius: '2px', padding: '1px', display: 'inline-flex', marginLeft: '5px' }}>
						<div style={{ width: '65%', height: '100%', background: '#4ade80', borderRadius: '1px' }} />
					</div>
				</div>
			</div>
			{/* App header */}
			<div style={{ background: '#08080f', padding: '8px 16px 10px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<img src="/logobanner.png" alt="Just Know" style={{ height: '22px', objectFit: 'contain', display: 'block' }} />
				<div style={{ width: '26px', height: '26px', borderRadius: '50%', background: `rgba(124,109,235,0.18)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<svg width="12" height="12" viewBox="0 0 24 24" fill={C.primary}><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
				</div>
			</div>
			{/* Feed */}
			<div style={{ background: '#09091a', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
				<span style={{ color: '#fff', fontSize: '13px', fontWeight: '800', letterSpacing: '-0.3px', marginBottom: '2px' }}>Breaking News</span>
				{[
					{ cat: 'POLITICS',   hl: 'Parliament passes landmark digital infrastructure bill', accent: C.primary, live: true },
					{ cat: 'BUSINESS',   hl: "India's EV market surges 40% — two-wheelers lead Q1 2026", accent: C.teal,    live: false },
					{ cat: 'TECHNOLOGY', hl: 'Bengaluru startup secures ₹800Cr in Series B round',        accent: C.primary, live: false },
				].map((item, i) => (
					<div key={i} style={{ background: '#0f0f22', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
						<div style={{ height: '62px', background: `linear-gradient(135deg, ${item.accent}1a, transparent)`, position: 'relative', padding: '8px' }}>
							<div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
								{item.live && <div style={{ width: '5px', height: '5px', background: '#ff3b30', borderRadius: '50%' }} />}
								<span style={{ background: item.accent, color: '#fff', fontSize: '8px', fontWeight: '700', padding: '2px 7px', borderRadius: '20px' }}>
									{item.live ? '● LIVE' : item.cat}
								</span>
							</div>
						</div>
						<div style={{ padding: '8px 10px 10px' }}>
							<p style={{ color: '#fff', fontSize: '10.5px', fontWeight: '600', lineHeight: 1.4, margin: 0 }}>{item.hl}</p>
						</div>
					</div>
				))}
			</div>
			{/* Bottom nav */}
			<div style={{ background: '#08080f', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-around', padding: '10px 0 14px' }}>
				{[
					{ label: 'Home',    active: true,  d: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z' },
					{ label: 'Latest',  active: false, d: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.5 5v5.25l4.5 2.67-.75 1.23L11 13V7h1.5z' },
					{ label: 'Saved',   active: false, d: 'M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z' },
					{ label: 'Profile', active: false, d: 'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' },
				].map(tab => (
					<div key={tab.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
						<svg width="15" height="15" viewBox="0 0 24 24" fill={tab.active ? C.primary : 'rgba(255,255,255,0.28)'}><path d={tab.d}/></svg>
						<span style={{ fontSize: '7.5px', color: tab.active ? C.primary : 'rgba(255,255,255,0.28)', fontWeight: tab.active ? '700' : '400' }}>{tab.label}</span>
					</div>
				))}
			</div>
		</div>
		{/* Floating badge — breaking */}
		<motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.1 }}
			style={{ position: 'absolute', top: '80px', right: '-52px', background: 'rgba(8,8,20,0.9)', backdropFilter: 'blur(16px)', border: `1px solid rgba(70,188,170,0.3)`, borderRadius: '10px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '7px' }}>
			<div style={{ width: '7px', height: '7px', background: C.teal, borderRadius: '50%', boxShadow: `0 0 6px ${C.teal}`, flexShrink: 0 }} />
			<span style={{ color: C.teal, fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap' }}>Live Breaking News</span>
		</motion.div>
		{/* Floating badge — offline */}
		<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3 }}
			style={{ position: 'absolute', bottom: '90px', left: '-52px', background: 'rgba(8,8,20,0.9)', backdropFilter: 'blur(16px)', border: `1px solid rgba(124,109,235,0.3)`, borderRadius: '10px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '7px' }}>
			<svg width="12" height="12" viewBox="0 0 24 24" fill={C.primary}><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/></svg>
			<span style={{ color: C.primary, fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap' }}>Available Offline</span>
		</motion.div>
	</motion.div>
);

// ─── Language Marquee ─────────────────────────────────────────────────────────
const LANG_FEED = [
	{ lang: 'తెలుగు',   text: 'రాష్ట్రంలో కొత్త పారిశ్రామిక విధానం ప్రకటన' },
	{ lang: 'हिंदी',    text: 'भारत में EV बाजार में रिकॉर्ड वृद्धि दर्ज' },
	{ lang: 'தமிழ்',   text: 'தமிழகத்தில் புதிய கொள்கை அறிவிப்பு' },
	{ lang: 'ಕನ್ನಡ',   text: 'ರಾಜ್ಯದಲ್ಲಿ ಹೊಸ ತಂತ್ರಜ್ಞಾನ ಯೋಜನೆ ಜಾರಿ' },
	{ lang: 'മലയാളം', text: 'കേരളത്തിൽ നൂതന സാങ്കേതിക നിക്ഷേപം' },
	{ lang: 'বাংলা',   text: 'বাংলায় নতুন শিক্ষা উদ্যোগ চালু করা হলো' },
	{ lang: 'मराठी',   text: 'महाराष्ट्रात नवीन उद्योग धोरण जाहीर' },
	{ lang: 'ગુજરાતી', text: 'ગુજરાતમાં ઉદ્યોગ ક્ષેત્રે નવો રેકોર્ડ' },
	{ lang: 'ਪੰਜਾਬੀ',  text: 'ਪੰਜਾਬ ਵਿੱਚ ਨਵੀਂ ਉਦਯੋਗਿਕ ਨੀਤੀ ਲਾਗੂ' },
	{ lang: 'ଓଡ଼ିଆ',   text: 'ଓଡ଼ିଶାରେ ନୂଆ ଶ୍ରମ ବିଭାଗ ଗଠନ' },
];
const LanguageMarquee: React.FC = () => (
	<div style={{ overflow: 'hidden', padding: '18px 0', position: 'relative' }}>
		<div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '80px', background: `linear-gradient(90deg, ${C.bg}, transparent)`, zIndex: 1, pointerEvents: 'none' }} />
		<div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '80px', background: `linear-gradient(270deg, ${C.bg}, transparent)`, zIndex: 1, pointerEvents: 'none' }} />
		<div className="lp-marquee-track" style={{ display: 'flex', width: 'max-content' }}>
			{[...LANG_FEED, ...LANG_FEED].map((item, i) => (
				<div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0 28px', flexShrink: 0 }}>
					<span style={{ color: C.primary, fontSize: '12px', fontWeight: '700' }}>{item.lang}</span>
					<span style={{ color: C.textSec, fontSize: '13px' }}>{item.text}</span>
					<span style={{ color: 'rgba(15,15,26,0.15)', fontSize: '18px', lineHeight: 1 }}>·</span>
				</div>
			))}
		</div>
	</div>
);

// ─── Feature Visuals ──────────────────────────────────────────────────────────
const ArticleSpotlight: React.FC = () => (
	<div style={{ position: 'relative', maxWidth: '420px' }}>
		<div style={{ background: C.surface, borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
			<div style={{ height: '140px', background: `linear-gradient(135deg, rgba(124,109,235,0.12) 0%, rgba(124,109,235,0.02) 100%)`, position: 'relative', display: 'flex', alignItems: 'flex-end', padding: '12px' }}>
				<span style={{ background: C.primary, color: '#fff', fontSize: '10px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', letterSpacing: '0.4px' }}>BUSINESS</span>
				<span style={{ marginLeft: 'auto', color: C.textMeta, fontSize: '11px' }}>2 Apr 2026</span>
			</div>
			<div style={{ padding: '20px' }}>
				<h4 style={{ color: C.textPri, fontSize: '16px', fontWeight: '700', lineHeight: 1.4, marginBottom: '12px', letterSpacing: '-0.2px' }}>
					India's EV market surges 40% in Q1 2026
				</h4>
				<p style={{ color: C.textSec, fontSize: '13.5px', lineHeight: 1.7, margin: '0 0 16px' }}>
					Electric vehicle sales hit a record high as government subsidies take effect. Two-wheeler adoption leads growth across major cities, with 60% penetration projected by 2028.
				</p>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: C.textMeta, fontSize: '12px' }}>
						<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
						Hyderabad
					</div>
					<span style={{ color: C.textMeta, fontSize: '12px' }}>1 min read</span>
				</div>
			</div>
		</div>
		{/* 60-word badge */}
		<div style={{ position: 'absolute', top: '-14px', right: '-14px', width: '54px', height: '54px', background: C.primary, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 24px rgba(124,109,235,0.45)` }}>
			<span style={{ color: '#fff', fontSize: '17px', fontWeight: '900', lineHeight: 1 }}>60</span>
			<span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '8px', fontWeight: '600' }}>words</span>
		</div>
	</div>
);

const OfflineVisual: React.FC = () => (
	<div style={{ background: C.surface, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px', maxWidth: '420px' }}>
		<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', padding: '12px 16px', background: 'rgba(0,0,0,0.03)', borderRadius: '10px', border: `1px solid ${C.border}` }}>
			<svg width="16" height="16" viewBox="0 0 24 24" fill="rgba(255,80,80,0.8)"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
			<div>
				<div style={{ color: C.textPri, fontSize: '13px', fontWeight: '600' }}>No internet connection</div>
				<div style={{ color: C.textMeta, fontSize: '12px' }}>3 stories downloaded and ready</div>
			</div>
		</div>
		{[
			{ title: 'Parliament passes digital infrastructure bill', time: '2h ago' },
			{ title: "India's EV surge — full Q1 2026 breakdown", time: '4h ago' },
			{ title: 'Bengaluru startup raises ₹800Cr Series B', time: '6h ago' },
		].map((item, i) => (
			<div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none' }}>
				<div>
					<div style={{ color: C.textPri, fontSize: '13px', fontWeight: '500', marginBottom: '3px' }}>{item.title}</div>
					<div style={{ color: C.textMeta, fontSize: '11px' }}>{item.time}</div>
				</div>
				<div style={{ width: '22px', height: '22px', borderRadius: '50%', background: `rgba(70,188,170,0.12)`, border: `1px solid rgba(70,188,170,0.25)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: '12px' }}>
					<svg width="10" height="10" viewBox="0 0 24 24" fill={C.teal}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
				</div>
			</div>
		))}
	</div>
);

const LanguageVisual: React.FC = () => {
	const langs = [
		{ name: 'తెలుగు', code: 'TE', active: true },
		{ name: 'हिंदी',   code: 'HI', active: false },
		{ name: 'தமிழ்',  code: 'TA', active: false },
		{ name: 'ಕನ್ನಡ',  code: 'KA', active: false },
		{ name: 'മലയാളം',code: 'ML', active: false },
		{ name: 'বাংলা',  code: 'BN', active: false },
		{ name: 'मराठी',  code: 'MR', active: false },
		{ name: 'ગુજરાતી',code: 'GU', active: false },
	];
	return (
		<div style={{ background: C.surface, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px', maxWidth: '420px' }}>
			<div style={{ color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '16px' }}>Choose your language</div>
			<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
				{langs.map(l => (
					<div key={l.code} style={{
						display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
						borderRadius: '10px',
						background: l.active ? `rgba(124,109,235,0.1)` : 'rgba(0,0,0,0.03)',
						border: l.active ? `1px solid rgba(124,109,235,0.35)` : `1px solid ${C.border}`,
					}}>
						<div style={{ width: '28px', height: '28px', borderRadius: '8px', background: l.active ? C.primary : 'rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
							<span style={{ color: l.active ? '#fff' : C.textMeta, fontSize: '9px', fontWeight: '700' }}>{l.code}</span>
						</div>
						<span style={{ color: l.active ? C.textPri : C.textSec, fontSize: '13px', fontWeight: l.active ? '600' : '400' }}>{l.name}</span>
					</div>
				))}
			</div>
		</div>
	);
};

const LocalVisual: React.FC = () => (
	<div style={{ background: C.surface, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '24px', maxWidth: '420px' }}>
		<div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
			<div style={{ width: '8px', height: '8px', background: C.primary, borderRadius: '50%', boxShadow: `0 0 8px rgba(124,109,235,0.7)` }} />
			<span style={{ color: C.primary, fontSize: '13px', fontWeight: '700' }}>Hyderabad, Telangana</span>
		</div>
		{[
			{ scope: 'Local',    color: C.primary,             headline: 'GHMC launches 100-km cycling track across Hyderabad' },
			{ scope: 'State',    color: C.teal,                headline: 'Telangana sets new paddy procurement record this season' },
			{ scope: 'National', color: 'rgba(15,15,26,0.3)', headline: 'Parliament passes landmark digital infrastructure bill' },
		].map((item, i) => (
			<div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '11px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none' }}>
				<span style={{ color: item.color, fontSize: '10px', fontWeight: '700', letterSpacing: '0.3px', paddingTop: '2px', minWidth: '52px' }}>{item.scope}</span>
				<p style={{ color: C.textSec, fontSize: '13px', lineHeight: 1.5, margin: 0 }}>{item.headline}</p>
			</div>
		))}
	</div>
);

// ─── Main Landing Page ────────────────────────────────────────────────────────
const LandingPage: React.FC = () => {
	const navigate        = useNavigate();
	const landingRef      = useRef<HTMLDivElement>(null);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const { scrollYProgress } = useScroll({ container: landingRef });
	const navBg      = useTransform(scrollYProgress, [0, 0.04], ['rgba(255,255,255,0)', 'rgba(255,255,255,0.97)']);
	const progressBar = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

	// ── Verify ──────────────────────────────────────────────────────────────────
	const [verifyCode,   setVerifyCode]   = useState('');
	const [verifyState,  setVerifyState]  = useState<'idle' | 'loading' | 'found' | 'notfound' | 'error'>('idle');
	const [verifyResult, setVerifyResult] = useState<{
		verificationCode: string; title: string | null; description: string | null;
		body: string | null; contentHash: string | null; publishedAt: string | null;
		primaryCategory: string | null; imageUrl: string | null; message: string;
	} | null>(null);

	const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.justknow.co.in';

	const handleVerify = useCallback(async () => {
		const code = verifyCode.trim().toUpperCase();
		if (!code) return;
		setVerifyState('loading');
		setVerifyResult(null);
		try {
			const res = await axios.get(`${API_BASE}/api/public/verify/${encodeURIComponent(code)}`, { timeout: 10_000, withCredentials: false });
			setVerifyResult(res.data);
			setVerifyState(res.data.verified ? 'found' : 'notfound');
		} catch (err: any) {
			setVerifyState(err?.response?.status === 400 ? 'notfound' : 'error');
		}
	}, [verifyCode, API_BASE]);

	// ── Contact ─────────────────────────────────────────────────────────────────
	const [contactForm,   setContactForm]   = useState({ name: '', email: '', subject: '', message: '' });
	const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
	const [contactError,  setContactError]  = useState('');

	const handleContactChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		setContactForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
	}, []);

	const handleContactSubmit = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		setContactStatus('sending');
		setContactError('');
		try {
			await axios.post(`${API_BASE}/api/public/contact`, contactForm);
			setContactStatus('success');
			setContactForm({ name: '', email: '', subject: '', message: '' });
		} catch (err: any) {
			setContactStatus('error');
			setContactError(err?.response?.data?.message || 'Something went wrong. Please try again.');
		}
	}, [contactForm, API_BASE]);

	// ── Helpers ─────────────────────────────────────────────────────────────────
	const handleScrollTo = useCallback((id: string) => {
		document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
		setMobileMenuOpen(false);
	}, []);

	// ── Data ────────────────────────────────────────────────────────────────────
	const navLinks = [
		{ label: 'Features',    id: 'features'  },
		{ label: 'App',         id: 'platform'  },
		{ label: 'How It Works',id: 'workflow'  },
		{ label: 'Stats',       id: 'stats'     },
		{ label: '🛡️ Verify',  id: 'verify'    },
		{ label: 'Contact',     id: 'contact'   },
	];

	const features: Array<{ tag: string; title: string; desc: string; bullets: string[]; visual: React.ReactNode }> = [
		{
			tag: 'Short Format',
			title: '60 words. The whole story.',
			desc: 'Every breaking story is distilled to under 60 words — verified facts, key context, no noise. Read a full headline in 20 seconds and move on with your day.',
			bullets: ['Verified facts only', 'Zero filler, zero opinion'],
			visual: <ArticleSpotlight />,
		},
		{
			tag: 'Offline-First',
			title: 'No internet? No problem.',
			desc: 'Stories sync automatically when you\'re online and stay readable without connectivity — on the metro, on a flight, in your village, anywhere your day takes you.',
			bullets: ['Auto-download on Wi-Fi', 'Zero connectivity needed offline', 'Works anywhere in India'],
			visual: <OfflineVisual />,
		},
		{
			tag: '15+ Indian Languages',
			title: 'Read in the language you think in.',
			desc: 'From Telugu to Tamil, Hindi to Kannada — Just Know delivers the same quality journalism in your preferred language. Every story, every day, in your script.',
			bullets: ['15+ Indian languages supported', 'Native script rendering', 'Switch language anytime'],
			visual: <LanguageVisual />,
		},
		{
			tag: 'Hyper-Local',
			title: 'Your city. Your district. Your state.',
			desc: 'Know what\'s happening three streets away and in Parliament — one intelligent feed that combines local, state, and national news tailored to your location.',
			bullets: ['City and district targeting', 'State + national in one feed'],
			visual: <LocalVisual />,
		},
	];

	const stats = [
		{ value: '50K+',  label: 'Daily Active Readers' },
		{ value: '10K+',  label: 'Stories Published Daily' },
		{ value: '60',    label: 'Words Per Story' },
		{ value: '15+',   label: 'Indian Languages' },
	];

	const workflowSteps = [
		{ n: '01', title: 'Set your preferences',   desc: 'Pick your language, categories, and location. Your personalised feed is ready in seconds.' },
		{ n: '02', title: 'Swipe through stories',  desc: 'Browse curated cards — every story under 60 words. Read the full picture in seconds.' },
		{ n: '03', title: 'Read, save, and share',  desc: 'Tap any card for the full story. Save to favourites or share with one tap.' },
		{ n: '04', title: 'Get breaking alerts',    desc: 'Instant push notifications for your chosen categories. Never miss what matters.' },
	];

	// ── Input shared style ───────────────────────────────────────────────────────
	const inputStyle: React.CSSProperties = {
		background: 'rgba(0,0,0,0.03)', border: `1px solid ${C.border}`,
		borderRadius: '10px', padding: '12px 16px', color: C.textPri,
		fontSize: '14px', outline: 'none', width: '100%', transition: 'border-color 0.2s', fontFamily: 'inherit',
	};

	return (
		<div
			ref={landingRef}
			style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowX: 'hidden', overflowY: 'auto', background: C.bg, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: C.textPri, zIndex: 9000 }}>

			{/* ── Scroll Progress ───────────────────────────────────────────── */}
			<motion.div style={{ position: 'fixed', top: 0, left: 0, height: '2px', zIndex: 9999, background: C.primary, width: progressBar }} />

			{/* ── Navigation ───────────────────────────────────────────────── */}
			<motion.nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, background: navBg, backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.border}`, padding: '0 24px' }}>
				<div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
					<motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}
						onClick={() => landingRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
						style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
						<img src="/logobanner.png" alt="Just Know" style={{ height: '34px', objectFit: 'contain', display: 'block' }} />
					</motion.div>

					{/* Desktop links */}
					<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
						className="landing-desktop-nav"
						style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
						{navLinks.map(link => (
							<button key={link.id} onClick={() => handleScrollTo(link.id)}
								style={{ background: 'none', border: 'none', color: C.textSec, fontSize: '14px', fontWeight: '500', cursor: 'pointer', padding: '6px 13px', borderRadius: '8px', transition: 'all 0.18s' }}
								onMouseEnter={e => { const t = e.target as HTMLElement; t.style.color = C.textPri; t.style.background = 'rgba(0,0,0,0.05)'; }}
								onMouseLeave={e => { const t = e.target as HTMLElement; t.style.color = C.textSec; t.style.background = 'none'; }}>
								{link.label}
							</button>
						))}
					</motion.div>

					{/* Download CTA + hamburger */}
					<motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
						style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
						<button onClick={() => handleScrollTo('features')}
							className="landing-desktop-nav"
							style={{ background: C.primary, border: 'none', color: '#fff', fontWeight: '600', fontSize: '13px', padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', transition: 'opacity 0.2s' }}
							onMouseEnter={e => { (e.target as HTMLElement).style.opacity = '0.85'; }}
							onMouseLeave={e => { (e.target as HTMLElement).style.opacity = '1'; }}>
							Get the App
						</button>
						<button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
							className="landing-mobile-menu-btn"
							style={{ background: 'rgba(0,0,0,0.04)', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'none', flexDirection: 'column', gap: '4px' }}>
							{[0,1,2].map(i => (
								<div key={i} style={{ width: '18px', height: '2px', background: C.textPri, transition: 'all 0.25s',
									transform: mobileMenuOpen ? (i === 0 ? 'rotate(45deg) translate(4px, 4px)' : i === 2 ? 'rotate(-45deg) translate(4px, -4px)' : 'scaleX(0)') : 'none',
									opacity: mobileMenuOpen && i === 1 ? 0 : 1 }} />
							))}
						</button>
					</motion.div>
				</div>

				{/* Mobile menu */}
				<AnimatePresence>
					{mobileMenuOpen && (
						<motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
							style={{ padding: '12px 24px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: '2px' }}>
							{navLinks.map(link => (
								<button key={link.id} onClick={() => handleScrollTo(link.id)}
									style={{ background: 'none', border: 'none', color: C.textSec, fontSize: '15px', padding: '10px 12px', textAlign: 'left', cursor: 'pointer', borderRadius: '8px', transition: 'color 0.18s' }}
									onMouseEnter={e => { (e.target as HTMLElement).style.color = C.textPri; }}
									onMouseLeave={e => { (e.target as HTMLElement).style.color = C.textSec; }}>
									{link.label}
								</button>
							))}
						</motion.div>
					)}
				</AnimatePresence>
			</motion.nav>

			{/* ── Hero ─────────────────────────────────────────────────────── */}
			<section className="lp-hero-section" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', padding: '100px 24px 72px', position: 'relative', overflow: 'hidden' }}>
				{/* Subtle gradient blob — top left only, no animation */}
				<div style={{ position: 'absolute', top: '-120px', left: '-80px', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(124,109,235,0.1) 0%, transparent 65%)', pointerEvents: 'none', borderRadius: '50%' }} />

				<div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
					<div className="lp-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '72px', alignItems: 'center' }}>

						{/* Left — Copy */}
						<motion.div initial="hidden" animate="visible" variants={staggerContainer}>
							<motion.div variants={fadeInUp}
								style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(124,109,235,0.1)', border: `1px solid rgba(124,109,235,0.2)`, borderRadius: '50px', padding: '5px 14px', marginBottom: '28px' }}>
								<span style={{ fontSize: '13px' }}>🇮🇳</span>
								<span style={{ color: C.primary, fontSize: '12px', fontWeight: '600', letterSpacing: '0.3px' }}>Built for India · 15+ Languages</span>
							</motion.div>

							<motion.h1 variants={fadeInUp}
								style={{ fontSize: 'clamp(40px, 5.5vw, 72px)', fontWeight: '900', lineHeight: 1.05, letterSpacing: '-3px', marginBottom: '24px' }}>
								News in{' '}
								<span style={{ background: `linear-gradient(135deg, ${C.primary} 0%, #a897f5 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
									60 words.
								</span>
								<br />In your language.
							</motion.h1>

							<motion.p variants={fadeInUp}
								style={{ color: C.textSec, fontSize: 'clamp(15px, 1.8vw, 17px)', lineHeight: 1.75, maxWidth: '460px', marginBottom: '36px' }}>
								Just Know delivers today's top stories — summarised under 60 words each, personalised to your location, available offline in 15+ Indian languages.
							</motion.p>

							<motion.div variants={fadeInUp} style={{ marginBottom: '32px' }}>
								<DownloadBadges size="md" />
							</motion.div>

							<motion.div variants={fadeInUp}
								style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
								<button onClick={() => handleScrollTo('features')}
									style={{ background: 'none', border: `1px solid ${C.border}`, color: C.textSec, fontSize: '14px', fontWeight: '500', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' }}
									onMouseEnter={e => { const t = e.target as HTMLElement; t.style.borderColor = 'rgba(0,0,0,0.2)'; t.style.color = C.textPri; }}
									onMouseLeave={e => { const t = e.target as HTMLElement; t.style.borderColor = C.border; t.style.color = C.textSec; }}>
									Explore Features
								</button>
								<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
									<div style={{ display: 'flex' }}>
										{['#7c6deb','#5b8dee','#46bcaa','#e86d96','#f0a047'].map((bg, i) => (
											<div key={i} style={{ width: '28px', height: '28px', borderRadius: '50%', background: bg, border: `2px solid ${C.bg}`, marginLeft: i > 0 ? '-8px' : 0 }} />
										))}
									</div>
									<div>
										<div style={{ color: C.textPri, fontWeight: '700', fontSize: '13px' }}>50,000+ readers</div>
										<div style={{ color: C.textMeta, fontSize: '12px' }}>Every morning</div>
									</div>
								</div>
							</motion.div>
						</motion.div>

						{/* Right — Phone */}
						<div className="lp-phone-section" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 60px' }}>
							<AppPreviewFrame />
						</div>
					</div>
				</div>
			</section>

			{/* ── Language Marquee ─────────────────────────────────────────── */}
			<div style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surface }}>
				<LanguageMarquee />
			</div>

			{/* ── Platform Strip ───────────────────────────────────────────── */}
			<section id="platform" style={{ padding: '64px 24px' }}>
				<div style={{ maxWidth: '1200px', margin: '0 auto' }}>
					<div style={{ textAlign: 'center', color: C.textMeta, fontSize: '11px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '40px' }}>
						Everything in one app
					</div>
					<motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
						className="lp-platform-grid"
						style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1px', background: C.border, borderRadius: '16px', overflow: 'hidden' }}>
						{[
							{ icon: '📱', label: 'iOS & Android',    sub: 'Both platforms' },
							{ icon: '📰', label: 'Short Stories',     sub: 'Under 60 words' },
							{ icon: '🔔', label: 'Breaking Alerts',  sub: 'Instant push' },
							{ icon: '📴', label: 'Offline Reading',  sub: 'No internet needed' },
							{ icon: '📍', label: 'Local News',       sub: 'City · State' },
							{ icon: '🌐', label: 'Multi-Language',   sub: '15+ Indian scripts' },
						].map((p, i) => (
							<motion.div key={i} variants={scaleIn}
								style={{ background: C.surface, padding: '24px 16px', textAlign: 'center' }}>
								<div style={{ fontSize: '26px', marginBottom: '10px' }}>{p.icon}</div>
								<div style={{ color: C.textPri, fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>{p.label}</div>
								<div style={{ color: C.textMeta, fontSize: '11px' }}>{p.sub}</div>
							</motion.div>
						))}
					</motion.div>
				</div>
			</section>

			{/* ── Features ─────────────────────────────────────────────────── */}
			<section id="features" style={{ padding: '80px 24px 100px' }}>
				<div style={{ maxWidth: '1160px', margin: '0 auto' }}>
					<motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={staggerContainer}
						style={{ marginBottom: '72px' }}>
						<motion.div variants={fadeInUp}
							style={{ display: 'inline-block', background: `rgba(124,109,235,0.1)`, border: `1px solid rgba(124,109,235,0.2)`, borderRadius: '50px', padding: '5px 14px', marginBottom: '18px' }}>
							<span style={{ color: C.primary, fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' }}>Why Just Know</span>
						</motion.div>
						<motion.h2 variants={fadeInUp}
							style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: '900', letterSpacing: '-1.5px', lineHeight: 1.15, maxWidth: '560px' }}>
							Built for people who want the news without the noise.
						</motion.h2>
					</motion.div>

					<div style={{ display: 'flex', flexDirection: 'column', gap: '80px' }}>
						{features.map((feat, i) => (
							<motion.div key={i}
								initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={staggerContainer}
								className="lp-feature-row"
								style={{ display: 'flex', flexDirection: i % 2 === 0 ? 'row' : 'row-reverse', gap: '72px', alignItems: 'center' }}>
								{/* Text */}
								<motion.div variants={fadeInUp} style={{ flex: 1, minWidth: 0 }}>
									<div style={{ display: 'inline-block', background: `rgba(124,109,235,0.08)`, border: `1px solid rgba(124,109,235,0.16)`, borderRadius: '50px', padding: '3px 12px', marginBottom: '16px' }}>
										<span style={{ color: C.primary, fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{feat.tag}</span>
									</div>
									<h3 style={{ fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: '800', letterSpacing: '-1px', lineHeight: 1.2, marginBottom: '16px', color: C.textPri }}>{feat.title}</h3>
									<p style={{ color: C.textSec, fontSize: '16px', lineHeight: 1.75, marginBottom: '24px' }}>{feat.desc}</p>
									<div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
										{feat.bullets.map((b, bi) => (
											<div key={bi} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
												<div style={{ width: '18px', height: '18px', borderRadius: '50%', background: `rgba(124,109,235,0.12)`, border: `1px solid rgba(124,109,235,0.25)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
													<svg width="9" height="9" viewBox="0 0 24 24" fill={C.primary}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
												</div>
												<span style={{ color: C.textSec, fontSize: '14px' }}>{b}</span>
											</div>
										))}
									</div>
								</motion.div>
								{/* Visual */}
								<motion.div variants={scaleIn} style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
									{feat.visual}
								</motion.div>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* ── Stats ────────────────────────────────────────────────────── */}
			<section id="stats" style={{ padding: '80px 24px', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: C.surface }}>
				<div style={{ maxWidth: '1160px', margin: '0 auto' }}>
					<motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={staggerContainer}
						style={{ marginBottom: '56px' }}>
						<motion.h2 variants={fadeInUp}
							style={{ fontSize: 'clamp(26px, 3.5vw, 40px)', fontWeight: '900', letterSpacing: '-1.5px', color: C.textPri }}>
							Numbers that speak for themselves.
						</motion.h2>
						<motion.p variants={fadeInUp} style={{ color: C.textSec, fontSize: '16px', marginTop: '12px' }}>
							Real readers. Real stories. Every single day.
						</motion.p>
					</motion.div>
					<motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }} variants={staggerContainer}
						className="lp-stats-grid"
						style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', background: C.border, borderRadius: '16px', overflow: 'hidden' }}>
						{stats.map((s, i) => (
							<motion.div key={i} variants={fadeInUp}
								style={{ background: C.bg, padding: '40px 32px' }}>
								<div style={{ fontSize: 'clamp(40px, 4.5vw, 56px)', fontWeight: '900', color: C.textPri, letterSpacing: '-2px', lineHeight: 1, marginBottom: '10px' }}>{s.value}</div>
								<div style={{ color: C.textMeta, fontSize: '13px', fontWeight: '500', letterSpacing: '0.2px' }}>{s.label}</div>
							</motion.div>
						))}
					</motion.div>
					{/* Secondary metrics */}
					<motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }} variants={staggerContainer}
						className="lp-metrics-grid"
						style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '16px' }}>
						{[
							{ metric: '< 2 min', label: 'Average read time per story' },
							{ metric: '20+',     label: 'News categories available' },
							{ metric: '24 / 7',  label: 'Fresh stories, always updated' },
						].map((item, i) => (
							<motion.div key={i} variants={scaleIn}
								style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px 28px', display: 'flex', gap: '20px', alignItems: 'center' }}>
								<div style={{ fontSize: '28px', fontWeight: '800', color: C.textPri, letterSpacing: '-1px', whiteSpace: 'nowrap' }}>{item.metric}</div>
								<div style={{ color: C.textMeta, fontSize: '13px', lineHeight: 1.5 }}>{item.label}</div>
							</motion.div>
						))}
					</motion.div>
				</div>
			</section>

			{/* ── How It Works ─────────────────────────────────────────────── */}
			<section id="workflow" style={{ padding: '100px 24px' }}>
				<div style={{ maxWidth: '1160px', margin: '0 auto' }}>
					<div className="lp-workflow-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'start' }}>
						{/* Left */}
						<motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={staggerContainer} style={{ position: 'sticky', top: '88px' }}>
							<motion.div variants={fadeInUp}
								style={{ display: 'inline-block', background: `rgba(70,188,170,0.1)`, border: `1px solid rgba(70,188,170,0.2)`, borderRadius: '50px', padding: '5px 14px', marginBottom: '18px' }}>
								<span style={{ color: C.teal, fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' }}>How It Works</span>
							</motion.div>
							<motion.h2 variants={fadeInUp}
								style={{ fontSize: 'clamp(26px, 3.5vw, 40px)', fontWeight: '900', letterSpacing: '-1.5px', lineHeight: 1.15, marginBottom: '20px', color: C.textPri }}>
								Informed in 4 simple steps.
							</motion.h2>
							<motion.p variants={fadeInUp} style={{ color: C.textSec, fontSize: '16px', lineHeight: 1.75, marginBottom: '36px' }}>
								No tutorials. No setup friction. Just open the app — your personalised feed is ready in under 30 seconds.
							</motion.p>
							<motion.div variants={fadeInUp} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
								{['🌏 World','📈 Business','🏏 Sports','💻 Tech','🎬 Entertainment','🏥 Health'].map((cat, i) => (
									<span key={i} style={{ color: C.textSec, fontSize: '12px', fontWeight: '600', background: 'rgba(0,0,0,0.04)', border: `1px solid ${C.border}`, borderRadius: '50px', padding: '5px 12px' }}>{cat}</span>
								))}
							</motion.div>
						</motion.div>
						{/* Right — Steps */}
						<motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={staggerContainer}
							style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
							{workflowSteps.map((step, i) => (
								<motion.div key={i} variants={fadeInUp}
									style={{ display: 'flex', gap: '20px', paddingBottom: i < workflowSteps.length - 1 ? '32px' : 0, position: 'relative' }}>
									{/* Step number + connector */}
									<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
										<div style={{ width: '44px', height: '44px', borderRadius: '50%', background: `rgba(124,109,235,0.1)`, border: `1px solid rgba(124,109,235,0.25)`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
											<span style={{ color: C.primary, fontSize: '13px', fontWeight: '800' }}>{step.n}</span>
										</div>
										{i < workflowSteps.length - 1 && (
											<div style={{ width: '1px', flex: 1, background: `linear-gradient(${C.border}, transparent)`, marginTop: '8px' }} />
										)}
									</div>
									<div style={{ paddingTop: '10px' }}>
										<h4 style={{ color: C.textPri, fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>{step.title}</h4>
										<p style={{ color: C.textSec, fontSize: '14px', lineHeight: 1.65, margin: 0 }}>{step.desc}</p>
									</div>
								</motion.div>
							))}
						</motion.div>
					</div>
				</div>
			</section>

			{/* ── Verify ───────────────────────────────────────────────────── */}
			<section id="verify" style={{ padding: '100px 24px', background: C.surface, borderTop: `1px solid ${C.border}` }}>
				<div style={{ maxWidth: '760px', margin: '0 auto' }}>
					<motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={staggerContainer}>
						<motion.div variants={fadeInUp} style={{ textAlign: 'center', marginBottom: '48px' }}>
							<div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: `rgba(70,188,170,0.1)`, border: `1px solid rgba(70,188,170,0.2)`, borderRadius: '50px', padding: '5px 14px', marginBottom: '20px' }}>
								<span>🛡️</span>
								<span style={{ color: C.teal, fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Content Verification</span>
							</div>
							<h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: '900', letterSpacing: '-1.5px', marginBottom: '14px', color: C.textPri }}>
								Verify a News Article
							</h2>
							<p style={{ color: C.textSec, fontSize: '15px', lineHeight: 1.7, maxWidth: '480px', margin: '0 auto' }}>
								Saw a Just Know article? Enter its verification code (e.g.&nbsp;
								<code style={{ background: 'rgba(0,0,0,0.07)', padding: '2px 7px', borderRadius: '4px', fontSize: '13px', fontFamily: 'monospace' }}>JK-550E8400</code>
								) to confirm it's genuine and unaltered.
							</p>
						</motion.div>

						<motion.div variants={fadeInUp} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '32px' }}>
							<input
								type="text" value={verifyCode} maxLength={16}
								onChange={e => { setVerifyCode(e.target.value); setVerifyState('idle'); setVerifyResult(null); }}
								onKeyDown={e => e.key === 'Enter' && handleVerify()}
								placeholder="Enter code, e.g. JK-550E8400"
								style={{ flex: '1 1 260px', maxWidth: '340px', background: 'rgba(0,0,0,0.03)', border: `1.5px solid ${C.border}`, borderRadius: '10px', padding: '13px 18px', color: C.textPri, fontSize: '15px', fontFamily: 'monospace', letterSpacing: '0.06em', outline: 'none', transition: 'border-color 0.2s' }}
								onFocus={e => { (e.target as HTMLInputElement).style.borderColor = C.teal; }}
								onBlur={e => { (e.target as HTMLInputElement).style.borderColor = C.border; }}
							/>
							<button onClick={handleVerify} disabled={verifyState === 'loading' || !verifyCode.trim()}
								style={{ background: `linear-gradient(135deg, ${C.teal}, #3aaa98)`, border: 'none', color: '#fff', fontWeight: '700', fontSize: '15px', padding: '13px 28px', borderRadius: '10px', cursor: verifyState === 'loading' ? 'not-allowed' : 'pointer', opacity: !verifyCode.trim() ? 0.5 : 1, transition: 'all 0.22s', whiteSpace: 'nowrap', boxShadow: `0 4px 20px rgba(70,188,170,0.3)` }}>
								{verifyState === 'loading' ? '⏳ Verifying…' : '🔍 Verify'}
							</button>
						</motion.div>

						<AnimatePresence>
							{verifyState === 'error' && (
								<motion.div key="error" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
									style={{ background: 'rgba(243,84,33,0.07)', border: '1px solid rgba(243,84,33,0.25)', borderRadius: '12px', padding: '20px 24px', textAlign: 'center', color: C.textSec, fontSize: '14px' }}>
									⚠️ Unable to reach the verification server. Please try again later.
								</motion.div>
							)}
							{verifyState === 'notfound' && (
								<motion.div key="notfound" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
									style={{ background: 'rgba(243,84,33,0.07)', border: '1px solid rgba(243,84,33,0.25)', borderRadius: '12px', padding: '24px 28px' }}>
									<div style={{ fontSize: '17px', fontWeight: '700', color: '#f35421', marginBottom: '10px' }}>❌ Article Not Found</div>
									<p style={{ color: C.textSec, fontSize: '14px', lineHeight: 1.7, marginBottom: '8px' }}>
										No published Just Know article matches code <code style={{ background: 'rgba(0,0,0,0.07)', padding: '2px 6px', borderRadius: '4px' }}>{verifyCode.trim().toUpperCase()}</code>.
									</p>
									<p style={{ color: C.textMeta, fontSize: '13px', margin: 0 }}>This content may be fabricated or the code may be incorrect. Do not share unverified content.</p>
								</motion.div>
							)}
							{verifyState === 'found' && verifyResult && (
								<motion.div key="found" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
									style={{ background: 'rgba(70,188,170,0.05)', border: `1px solid rgba(70,188,170,0.22)`, borderRadius: '16px', overflow: 'hidden' }}>
									<div style={{ background: 'rgba(70,188,170,0.12)', padding: '13px 24px', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: `1px solid rgba(70,188,170,0.14)` }}>
										<span style={{ fontSize: '18px' }}>✅</span>
										<span style={{ color: C.teal, fontWeight: '700', fontSize: '14px' }}>Verified — Genuine Just Know content</span>
										<span style={{ marginLeft: 'auto', background: 'rgba(70,188,170,0.15)', border: `1px solid rgba(70,188,170,0.25)`, borderRadius: '20px', padding: '3px 11px', color: C.teal, fontSize: '12px', fontWeight: '600', fontFamily: 'monospace' }}>{verifyResult.verificationCode}</span>
									</div>
									<div style={{ display: 'flex', flexWrap: 'wrap' }}>
										{verifyResult.imageUrl && (
											<div style={{ width: '240px', minWidth: '200px', flexShrink: 0 }}>
												<img src={verifyResult.imageUrl} alt={verifyResult.title || 'Article image'}
													style={{ width: '100%', height: '100%', minHeight: '180px', objectFit: 'cover', display: 'block' }}
													onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
											</div>
										)}
										<div style={{ flex: 1, minWidth: '220px', padding: '24px' }}>
											{verifyResult.primaryCategory && (
												<div style={{ display: 'inline-block', background: `rgba(124,109,235,0.15)`, border: `1px solid rgba(124,109,235,0.25)`, borderRadius: '10px', padding: '3px 11px', color: '#a897f5', fontSize: '11px', fontWeight: '600', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
													{verifyResult.primaryCategory}
												</div>
											)}
											<h3 style={{ fontSize: '19px', fontWeight: '800', lineHeight: 1.35, letterSpacing: '-0.4px', marginBottom: '12px', color: C.textPri }}>
												{verifyResult.title || '—'}
											</h3>
											{verifyResult.body && (
												<p style={{ color: C.textSec, fontSize: '13.5px', lineHeight: 1.7, marginBottom: '18px' }}>
													{verifyResult.body.length > 500 ? verifyResult.body.substring(0, 500) + '…' : verifyResult.body}
												</p>
											)}
											{verifyResult.publishedAt && (
												<div style={{ color: C.textMeta, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
													🗓️ {new Date(((verifyResult.publishedAt as string).endsWith('Z') || (verifyResult.publishedAt as string).includes('+')) ? verifyResult.publishedAt as string : (verifyResult.publishedAt as string) + 'Z').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
												</div>
											)}
										</div>
									</div>
								</motion.div>
							)}
						</AnimatePresence>
					</motion.div>
				</div>
			</section>

			{/* ── CTA ──────────────────────────────────────────────────────── */}
			<section style={{ padding: '100px 24px' }}>
				<div style={{ maxWidth: '860px', margin: '0 auto' }}>
					<motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={staggerContainer}
						style={{ textAlign: 'center', background: `linear-gradient(135deg, rgba(124,109,235,0.12) 0%, rgba(124,109,235,0.04) 100%)`, border: `1px solid rgba(124,109,235,0.18)`, borderRadius: '24px', padding: 'clamp(40px, 6vw, 72px) clamp(24px, 5vw, 64px)', position: 'relative', overflow: 'hidden' }}>
						<div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: 'radial-gradient(circle, rgba(124,109,235,0.2), transparent 65%)', borderRadius: '50%', pointerEvents: 'none' }} />
						<motion.h2 variants={fadeInUp}
							style={{ fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: '900', letterSpacing: '-1.5px', marginBottom: '16px', color: C.textPri, position: 'relative' }}>
							Ready to stay informed, smarter?
						</motion.h2>
						<motion.p variants={fadeInUp}
							style={{ color: C.textSec, fontSize: '16px', lineHeight: 1.7, maxWidth: '480px', margin: '0 auto 36px' }}>
							Join 50,000+ readers who start their day with Just Know — in the language they love.
						</motion.p>
						<motion.div variants={fadeInUp} style={{ display: 'flex', justifyContent: 'center' }}>
							<DownloadBadges size="md" />
						</motion.div>
					</motion.div>
				</div>
			</section>

			{/* ── Contact ──────────────────────────────────────────────────── */}
			<section id="contact" style={{ padding: '100px 24px', background: C.surface, borderTop: `1px solid ${C.border}` }}>
				<div style={{ maxWidth: '620px', margin: '0 auto' }}>
					<motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={staggerContainer}>
						<motion.div variants={fadeInUp} style={{ textAlign: 'center', marginBottom: '48px' }}>
							<span style={{ display: 'inline-block', background: `rgba(124,109,235,0.1)`, border: `1px solid rgba(124,109,235,0.2)`, borderRadius: '50px', padding: '5px 14px', fontSize: '11px', fontWeight: '700', color: C.primary, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '16px' }}>Get In Touch</span>
							<h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: '900', letterSpacing: '-1px', marginBottom: '12px', color: C.textPri }}>Contact Us</h2>
							<p style={{ color: C.textSec, fontSize: '15px', lineHeight: 1.7 }}>Questions, feedback, or partnership inquiries — we'd love to hear from you.</p>
						</motion.div>

						<motion.form variants={fadeInUp} onSubmit={handleContactSubmit}
							style={{ background: 'rgba(0,0,0,0.02)', border: `1px solid ${C.border}`, borderRadius: '18px', padding: 'clamp(24px, 4vw, 40px)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
								<div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
									<label style={{ fontSize: '12px', fontWeight: '600', color: C.textSec }}>Your Name *</label>
									<input name="name" value={contactForm.name} onChange={handleContactChange} required placeholder="e.g. Ravi Kumar"
										style={inputStyle}
										onFocus={e => { e.target.style.borderColor = 'rgba(124,109,235,0.55)'; }}
										onBlur={e => { e.target.style.borderColor = C.border; }} />
								</div>
								<div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
									<label style={{ fontSize: '12px', fontWeight: '600', color: C.textSec }}>Email Address *</label>
									<input name="email" type="email" value={contactForm.email} onChange={handleContactChange} required placeholder="you@example.com"
										style={inputStyle}
										onFocus={e => { e.target.style.borderColor = 'rgba(124,109,235,0.55)'; }}
										onBlur={e => { e.target.style.borderColor = C.border; }} />
								</div>
							</div>
							<div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
								<label style={{ fontSize: '12px', fontWeight: '600', color: C.textSec }}>Subject *</label>
								<select name="subject" value={contactForm.subject} onChange={handleContactChange} required
									style={{ ...inputStyle, background: '#f0f1f7', cursor: 'pointer', color: contactForm.subject ? C.textPri : C.textMeta }}
									onFocus={e => { e.target.style.borderColor = 'rgba(124,109,235,0.55)'; }}
									onBlur={e => { e.target.style.borderColor = C.border; }}>
									<option value="" disabled>Select a subject</option>
									<option value="General Inquiry">General Inquiry</option>
									<option value="App Feedback">App Feedback</option>
									<option value="Report a Bug">Report a Bug</option>
									<option value="Partnership / Advertising">Partnership / Advertising</option>
									<option value="Media / Press">Media / Press</option>
									<option value="Account Support">Account Support</option>
									<option value="Other">Other</option>
								</select>
							</div>
							<div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
								<label style={{ fontSize: '12px', fontWeight: '600', color: C.textSec }}>Message *</label>
								<textarea name="message" value={contactForm.message} onChange={handleContactChange} required rows={5} placeholder="Tell us how we can help..."
									style={{ ...inputStyle, resize: 'vertical' }}
									onFocus={e => { e.target.style.borderColor = 'rgba(124,109,235,0.55)'; }}
									onBlur={e => { e.target.style.borderColor = C.border; }} />
							</div>
							{contactStatus === 'success' && (
								<div style={{ background: 'rgba(70,188,170,0.1)', border: `1px solid rgba(70,188,170,0.25)`, borderRadius: '9px', padding: '13px 16px', color: C.teal, fontSize: '14px', textAlign: 'center' }}>
									✓ Message sent! We'll get back to you within 1–2 business days.
								</div>
							)}
							{contactStatus === 'error' && (
								<div style={{ background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.22)', borderRadius: '9px', padding: '13px 16px', color: '#ff6b6b', fontSize: '14px', textAlign: 'center' }}>
									{contactError}
								</div>
							)}
							<button type="submit" disabled={contactStatus === 'sending'}
								style={{ background: contactStatus === 'sending' ? `rgba(124,109,235,0.45)` : C.primary, border: 'none', borderRadius: '10px', padding: '13px 28px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: contactStatus === 'sending' ? 'not-allowed' : 'pointer', transition: 'all 0.25s' }}
								onMouseEnter={e => { if (contactStatus !== 'sending') (e.currentTarget as HTMLElement).style.opacity = '0.85'; }}
								onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}>
								{contactStatus === 'sending' ? 'Sending…' : 'Send Message'}
							</button>
						</motion.form>

						<motion.p variants={fadeInUp} style={{ textAlign: 'center', color: C.textMeta, fontSize: '13px', marginTop: '20px' }}>
							Or email us at{' '}
							<a href="mailto:contact@justknow.co.in" style={{ color: '#a897f5', textDecoration: 'none' }}>contact@justknow.co.in</a>
						</motion.p>
					</motion.div>
				</div>
			</section>

			{/* ── Footer ───────────────────────────────────────────────────── */}
			<footer style={{ padding: '44px 24px 32px', borderTop: `1px solid ${C.border}` }}>
				<div style={{ maxWidth: '1200px', margin: '0 auto' }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '24px', marginBottom: '28px' }}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<img src="/logobanner.png" alt="Just Know" style={{ height: '30px', objectFit: 'contain', display: 'block' }} />
							<span style={{ color: C.textMeta, fontSize: '11px' }}>Short News App</span>
						</div>
						<div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
							{navLinks.map(link => (
								<button key={link.id} onClick={() => handleScrollTo(link.id)}
									style={{ background: 'none', border: 'none', color: C.textMeta, fontSize: '13px', cursor: 'pointer', padding: 0, transition: 'color 0.18s' }}
									onMouseEnter={e => { (e.target as HTMLElement).style.color = C.textPri; }}
									onMouseLeave={e => { (e.target as HTMLElement).style.color = C.textMeta; }}>
									{link.label}
								</button>
							))}
						</div>
					</div>
					<div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
						<span style={{ color: C.textMeta, fontSize: '13px' }}>© {new Date().getFullYear()} Just Know. All rights reserved.</span>
						<span style={{ color: C.textMeta, fontSize: '12px' }}>Short News · Stay Informed</span>
					</div>
				</div>
			</footer>

			{/* ── Global Styles ─────────────────────────────────────────────── */}
			<style>{`
				@keyframes lp-marquee {
					from { transform: translateX(0); }
					to   { transform: translateX(-50%); }
				}
				* { margin: 0; padding: 0; box-sizing: border-box; }
				html, body { overflow-x: hidden; }
				html { scroll-behavior: smooth; }
				::-webkit-scrollbar { width: 5px; }
				::-webkit-scrollbar-track { background: #f4f5f9; }
				::-webkit-scrollbar-thumb { background: rgba(124,109,235,0.35); border-radius: 3px; }
				::-webkit-scrollbar-thumb:hover { background: rgba(124,109,235,0.6); }

				.lp-marquee-track { animation: lp-marquee 38s linear infinite; }
				.lp-marquee-track:hover { animation-play-state: paused; }

				/* ── Breakpoint: nav ───────────────────── */
				@media (max-width: 900px) {
					.landing-desktop-nav { display: none !important; }
					.landing-mobile-menu-btn { display: flex !important; }
				}

				/* ── Breakpoint: tablet (≤768px) ───────── */
				@media (max-width: 768px) {
					.lp-hero-section {
						align-items: flex-start !important;
						padding-top: 88px !important;
						padding-bottom: 48px !important;
					}
					.lp-hero-grid {
						grid-template-columns: 1fr !important;
						gap: 40px !important;
					}
					.lp-phone-section {
						display: none !important;
					}
					.lp-feature-row {
						flex-direction: column !important;
						gap: 32px !important;
					}
					.lp-workflow-grid {
						grid-template-columns: 1fr !important;
						gap: 40px !important;
					}
					.lp-stats-grid {
						grid-template-columns: repeat(2, 1fr) !important;
					}
					.lp-platform-grid {
						grid-template-columns: repeat(3, 1fr) !important;
					}
					.lp-metrics-grid {
						grid-template-columns: 1fr !important;
					}
				}

				/* ── Breakpoint: mobile (≤480px) ───────── */
				@media (max-width: 480px) {
					.lp-stats-grid {
						grid-template-columns: repeat(2, 1fr) !important;
					}
					.lp-platform-grid {
						grid-template-columns: repeat(2, 1fr) !important;
					}
				}
			`}</style>
		</div>
	);
};

export default LandingPage;
